import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import Logger from '../Services/loggerService';
import BillingService from '../Services/billingService';

const CACHE_THRESHOLD = 60 * 60 * 1000; // 1 hora


const BillingLoadingContext = createContext();

export const useBillingLoading = () => {
    const context = useContext(BillingLoadingContext);
    if (!context) {
        throw new Error('useBillingLoading debe ser usado dentro de un BillingLoadingProvider');
    }
    return context;
};

export const BillingLoadingProvider = ({ children }) => {
    const [isBillingLoading, setIsBillingLoading] = useState(false);
    const [billingProgress, setBillingProgress] = useState(0);
    const [billingStatus, setBillingStatus] = useState('idle'); // 'idle', 'loading', 'completed', 'error'
    const [billingError, setBillingError] = useState(null);
    const [lastBillingUpdate, setLastBillingUpdate] = useState(null);
    const [userNit, setUserNit] = useState(null);

    // Función para obtener NIT del usuario
    const getCurrentNit = useCallback(() => {
        try {
            const nitFromCookie = document.cookie
                .split('; ')
                .find(row => row.startsWith('nit='))
                ?.split('=')[1];

            const nitFromSession = sessionStorage.getItem('nit');
            const nitFromLocal = localStorage.getItem('nit');

            return nitFromCookie || nitFromSession || nitFromLocal;
        } catch (error) {
            Logger.error('Error obteniendo NIT:', error);
            return null;
        }
    }, []);

    // Función para actualizar progreso
    const updateBillingProgress = useCallback((progress, status = null) => {
        setBillingProgress(Math.min(100, Math.max(0, progress)));
        if (status) {
            setBillingStatus(status);
        }
        Logger.billing('Progreso de carga actualizado', { progress, status });
    }, []);

    // Función para completar carga
    const completeBillingLoad = useCallback((success = true, error = null) => {
        setIsBillingLoading(false);
        setBillingProgress(100);
        setBillingStatus(success ? 'completed' : 'error');
        setBillingError(error);
        setLastBillingUpdate(Date.now());

        Logger.billing('Carga de facturas completada', {
            success,
            error: error?.message,
            timestamp: Date.now()
        });

        // Resetear progreso después de un tiempo
        setTimeout(() => {
            setBillingProgress(0);
            setBillingStatus('idle');
        }, 3000);
    }, []);

    // Función para manejar errores
    const handleBillingError = useCallback((error) => {
        Logger.error('Error en carga de facturas:', error);
        completeBillingLoad(false, error);
    }, [completeBillingLoad]);

    // Función para iniciar carga de facturas (vista simple)
    const startBillingLoad = useCallback((nit = null) => {
        const currentNit = nit || getCurrentNit();
        if (!currentNit) {
            Logger.error('No se pudo obtener NIT para cargar facturas');
            return;
        }

        setUserNit(currentNit);
        setIsBillingLoading(true);
        setBillingStatus('loading');
        setBillingProgress(0);
        setBillingError(null);
    }, [getCurrentNit]);

    // Función para disparar sincronización completa con SSE
    const triggerWonderPointsSync = useCallback(async (nit = null) => {
        const currentNit = nit || getCurrentNit();
        if (!currentNit) return;

        setUserNit(currentNit);
        setIsBillingLoading(true);
        setBillingStatus('loading');
        setBillingProgress(0);
        setBillingError(null);

        try {
            Logger.billing('Iniciando sincronización completa SSE desde contexto');
            
            await BillingService.syncWonderPointsSSE((progress, message, status) => {
                if (progress !== null) setBillingProgress(progress);
                if (status) setBillingStatus(status);
                
                if (message) {
                    Logger.billing(`[SSE Sync] ${message} (${progress}%)`);
                }
            });
            
            // Notificar éxito y refrescar
            completeBillingLoad(true);
            
            // Disparar evento para que los componentes suscritos recarguen sus datos locales
            window.dispatchEvent(new CustomEvent('billingDataReload', { 
                detail: { nit: currentNit, source: 'auto-sync' } 
            }));

        } catch (error) {
            Logger.error('Error en sincronización automática:', error);
            handleBillingError(error);
        }
    }, [getCurrentNit, completeBillingLoad, handleBillingError]);


    // Función para resetear estado
    const resetBillingState = useCallback(() => {
        setIsBillingLoading(false);
        setBillingProgress(0);
        setBillingStatus('idle');
        setBillingError(null);
        setUserNit(null);
        Logger.billing('Estado de carga de facturas reseteado');
    }, []);

    // Verificar si hay datos de facturas en caché al inicializar o al cambiar de usuario
    useEffect(() => {
        const checkCachedBillingData = async () => {
            try {
                const nit = getCurrentNit();
                if (!nit) {
                    Logger.billing('No hay NIT disponible para verificar caché');
                    return;
                }

                // Obtener rol del usuario (para validación solicitada)
                const storedRole = localStorage.getItem('userRole') || 
                                 sessionStorage.getItem('userRole') || 
                                 sessionStorage.getItem('role') ||
                                 (typeof document !== 'undefined' ? document.cookie.split('; ').find(row => row.startsWith('userRole='))?.split('=')[1] : null) ||
                                 '';
                const userRole = String(storedRole).toLowerCase().trim();
                const isAdmin = userRole === 'admin' || userRole === 'administrador';

                // Verificar si acabamos de loguearnos (forzar sync)
                const justLoggedIn = sessionStorage.getItem('justLoggedIn') === 'true';

                // Verificar si hay datos usando el servicio (que ya maneja IndexedDB y memoria)
                const cachedData = await BillingService.getBillingData(nit, false);

                if (justLoggedIn && !isAdmin) {
                    Logger.billing('Detectado inicio de sesión reciente, forzando sincronización completa');
                    sessionStorage.removeItem('justLoggedIn');
                    triggerWonderPointsSync(nit);
                    return;
                }

                if (cachedData && cachedData.data) {
                    const cacheAge = Date.now() - cachedData.timestamp;
                    const isStale = cacheAge > CACHE_THRESHOLD;

                    if (!isStale) {
                        Logger.billing('Datos encontrados y frescos en caché', {
                            nit,
                            cacheAge: Math.round(cacheAge / 1000 / 60) + ' minutos'
                        });
                        setBillingStatus('completed');
                        setLastBillingUpdate(cachedData.timestamp);
                        setIsBillingLoading(false);
                    } else if (!isAdmin) {
                        // SI NO ES ADMIN y los datos están viejos -> Sincronizar automáticamente
                        Logger.billing('Datos en caché obsoletos para usuario final, iniciando auto-sync');
                        triggerWonderPointsSync(nit);
                    } else {
                        // SI ES ADMIN -> NO sincronizar automático, dejar que él lo haga manual
                        Logger.billing('Datos en caché obsoletos pero es ADMIN, skipping auto-sync');
                        setBillingStatus('completed'); // Mostrar lo que hay aunque sea viejo
                        setLastBillingUpdate(cachedData.timestamp);
                    }
                } else if (!isAdmin) {
                    // Si no hay datos Y NO ES ADMIN -> Primera sincronización
                    Logger.billing('No hay caché de facturas para usuario final, iniciando primera sincronización');
                    triggerWonderPointsSync(nit);
                }
            } catch (error) {
                Logger.error('Error verificando frescura de datos:', error);
            }
        };

        const timer = setTimeout(checkCachedBillingData, 1500);
        return () => clearTimeout(timer);
    }, [getCurrentNit, triggerWonderPointsSync]);

    // Escuchar evento de logout
    useEffect(() => {
        const handleLogout = () => {
            Logger.billing('Logout detectado, reseteando estado de facturas');
            resetBillingState();
        };

        window.addEventListener('userLogout', handleLogout);
        return () => {
            window.removeEventListener('userLogout', handleLogout);
        };
    }, [resetBillingState]);



    const value = {
        // Estados
        isBillingLoading,
        billingProgress,
        billingStatus,
        billingError,
        lastBillingUpdate,
        userNit,

        // Funciones
        startBillingLoad,
        triggerWonderPointsSync,
        updateBillingProgress,
        completeBillingLoad,
        handleBillingError,
        resetBillingState,


        // Utilidades
        isCompleted: billingStatus === 'completed',
        hasError: billingStatus === 'error',
        isIdle: billingStatus === 'idle',

        // Debug
        debug: () => {
            Logger.billing('Estado de carga de facturas:', {
                isBillingLoading,
                billingProgress,
                billingStatus,
                billingError: billingError?.message,
                userNit,
                lastBillingUpdate
            });
        }
    };

    return (
        <BillingLoadingContext.Provider value={value}>
            {children}
        </BillingLoadingContext.Provider>
    );
};

export default BillingLoadingContext; 